import {AssetType, DocumentType, PartyRole, PayoutType, TrustPurpose} from '@sollapay/enums';
import {Money, ZERO} from '@sollapay/utils/money';

import {StandardTrustType} from './trust-type.strategy';

import type {AmountDueParams, TrustScopeRequiredField} from './trust-type.strategy';

/** The 19 general-sale property types — every AssetType except the developer-sale unit. */
export const GENERAL_SALE_ASSET_TYPES: AssetType[] = Object.values(AssetType).filter(
  assetType => assetType !== AssetType.DEVELOPER_UNIT
);

/** מכר כללי — the whole consideration passes through the trust (uncapped), so amount due is what is outstanding. */
export class GeneralSaleSecondHandTrustType extends StandardTrustType {
  readonly purpose = TrustPurpose.GENERAL_SALE;
  readonly maxCollectionPercentage = 100;

  /**
   * The asset price is deal metadata, not a payin ceiling — a lawyer must be able to request
   * payins beyond it (e.g. a foreign buyer routing a purchase-tax amount through the trust)
   * without a workaround. See SOL-500.
   */
  override readonly enforcesCollectionCap = false;

  getBuyerPartyRoles(): PartyRole[] {
    return [PartyRole.BUYER, PartyRole.BENEFICIARY];
  }

  override readonly sellerSideRole = PartyRole.SELLER;

  /** Only what remains outstanding, floored at zero once fully paid (or overpaid). */
  override computeAmountDue({maxCollectable, depositPaid}: AmountDueParams): Money {
    return Money.max(maxCollectable.subtract(depositPaid), ZERO);
  }

  override getRequiredComplianceDocumentTypes(): DocumentType[] {
    return [DocumentType.LAND_OWNERSHIP_VERIFICATION, DocumentType.TRUSTEE_INSTRUCTIONS];
  }

  /** No buyer registration form — that form is specific to developer sales. */
  override getOptionalPurchaseDocumentTypes(): DocumentType[] {
    return [];
  }

  /** Exactly one asset — types coding as a bare prefix (e.g. HSE) would collide on the display_code index if true. */
  override readonly allowsMultipleAssets = false;

  /** One contract over one property: the purchase is created at approval, priced from the asset's
   * reference price, and every buyer ever added joins it. */
  override readonly createsPurchaseAtActivation: boolean = true;

  override readonly allowsMultiplePurchases: boolean = false;

  /** Co-owners, heirs, or a mixed individual/company group can all sell one asset together. */
  override readonly allowsMultipleSellerSideParties = true;

  /** The seller is a one-off counterparty, not an ongoing relationship like a developer. */
  override readonly asksSellerSideRelationshipQuestions = false;

  /** הוסף צד adds these roles inline in the beneficiaries table; מוכר/קונה are never offered there. */
  override readonly inlineAddableBeneficiaryRoles = [PartyRole.SUPPLIER, PartyRole.TRUSTEE];

  /** The only deal type with a payout workflow today. */
  override readonly showsPayoutsTab = true;

  /** Co-buyers on a shared purchase each pay separately, so a buyer's page lists only their own. */
  override readonly scopesPaymentHistoryToBuyer = true;

  override getRequiredScopeFields(): TrustScopeRequiredField[] {
    return ['transactionType'];
  }

  override getAllowedAssetTypes(): AssetType[] {
    return GENERAL_SALE_ASSET_TYPES;
  }

  override readonly requiresAssetAtActivation: boolean = true;

  override getAssetPropertyDocumentTypes(): DocumentType[] {
    return [DocumentType.SIGNED_SALE_AGREEMENT];
  }

  /** No funded bank here — a second-hand sale has no developer construction loan to service. */
  override getPayoutTypeOptions(): PayoutType[] {
    return [
      PayoutType.SELLER_BANK_ACCOUNT,
      PayoutType.SUPPLIER,
      PayoutType.TRUSTEE,
      PayoutType.AUTHORITIES,
      PayoutType.OTHER
    ];
  }
}

export const generalSaleSecondHandTrustType = new GeneralSaleSecondHandTrustType();
